import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, Plus, Trash2, Save, Loader2 } from "lucide-react";

const FIELD_TYPES = [
    { v: "text", l: "Short text" },
    { v: "textarea", l: "Long text" },
    { v: "date", l: "Date" },
    { v: "select", l: "Dropdown" },
    { v: "acknowledge", l: "Acknowledge (checkbox)" },
    { v: "signature", l: "Signature block" },
];

export default function DocumentTemplateBuilderPage() {
    const { id } = useParams();
    const [tpl, setTpl] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/documents/templates/${id}`).then(({ data }) => setTpl(data));
    }, [id]);

    if (!tpl) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

    const update = (patch) => setTpl({ ...tpl, ...patch });
    const updateField = (i, patch) => {
        const fields = [...tpl.fields];
        fields[i] = { ...fields[i], ...patch };
        update({ fields });
    };
    const removeField = (i) => update({ fields: tpl.fields.filter((_, j) => j !== i) });
    const addField = () => update({ fields: [...(tpl.fields || []), { id: `f_${Date.now()}`, label: "New field", type: "text", required: false, options: [] }] });

    const save = async () => {
        setSaving(true); setErr("");
        try {
            await api.put(`/documents/templates/${id}`, {
                title: tpl.title,
                description: tpl.description || "",
                fields: tpl.fields || [],
                is_default: tpl.is_default || false,
                require_signature: tpl.require_signature !== false,
            });
            setSavedAt(new Date());
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setSaving(false); }
    };

    return (
        <div className="p-8 space-y-6" data-testid="doc-builder-page">
            <div className="flex items-end justify-between gap-4">
                <div className="flex-1">
                    <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--gold)] mb-3"><ChevronLeft size={14} /> Documents</Link>
                    <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none font-serif text-5xl w-full"
                        value={tpl.title} onChange={(e) => update({ title: e.target.value })} data-testid="tpl-title" />
                    <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none text-sm text-[var(--text-muted)] mt-2 w-full"
                        placeholder="Description…" value={tpl.description || ""} onChange={(e) => update({ description: e.target.value })} data-testid="tpl-description" />
                </div>
                <div className="flex items-center gap-3">
                    {savedAt && <span className="font-mono text-xs text-[var(--text-muted)]">saved {savedAt.toLocaleTimeString()}</span>}
                    <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-tpl">
                        <Save size={14} /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={tpl.require_signature !== false} onChange={(e) => update({ require_signature: e.target.checked })} />
                Require typed signature on submit
            </label>

            <div className="space-y-3">
                {(tpl.fields || []).map((f, i) => (
                    <div key={f.id} className="border border-[var(--border)] bg-[var(--surface)] p-4" data-testid={`field-${i}`}>
                        <div className="grid grid-cols-12 gap-3 items-start">
                            <input className="input-dark col-span-4" placeholder="Label" value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} />
                            <select className="input-dark col-span-3" value={f.type} onChange={(e) => updateField(i, { type: e.target.value })}>
                                {FIELD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                            </select>
                            <label className="col-span-2 inline-flex items-center gap-2 text-sm pt-2">
                                <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} />
                                Required
                            </label>
                            <input className="input-dark col-span-2" placeholder="Placeholder" value={f.placeholder || ""} onChange={(e) => updateField(i, { placeholder: e.target.value })} />
                            <button onClick={() => removeField(i)} className="col-span-1 text-[var(--text-muted)] hover:text-[var(--danger)] flex justify-center pt-2" data-testid={`del-field-${i}`}><Trash2 size={14} /></button>
                        </div>
                        {f.type === "select" && (
                            <input className="input-dark mt-3" placeholder="Options (comma-separated)"
                                value={(f.options || []).join(", ")}
                                onChange={(e) => updateField(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
                        )}
                    </div>
                ))}
                <button onClick={addField} className="w-full border border-dashed border-[var(--border-bright)] p-6 text-[var(--text-muted)] hover:text-[var(--gold)] hover:border-[var(--gold)] inline-flex items-center justify-center gap-2" data-testid="add-field">
                    <Plus size={14} /> Add field
                </button>
            </div>
            {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
        </div>
    );
}
